<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import TemplateCard from './components/TemplateCard.vue';
import TemplateForm from './components/TemplateForm.vue';
import TemplateTable from './components/TemplateTable.vue';
import TemplateDetail from './components/TemplateDetail.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const wabaId = computed(
  () => props.inbox.provider_config?.business_account_id || ''
);
const metaBusinessUrl = computed(
  () =>
    `https://business.facebook.com/wa/manage/message-templates/?waba_id=${wabaId.value}&templates_tab=category_updates`
);

// State
const templates = ref([]);
const analyticsByTemplate = ref({});
const isLoading = ref(false);
const isSubmitting = ref(false);
const isSyncing = ref(false);
const activeFilter = ref('ALL');
const currentView = ref('list'); // list | detail | create | edit
const viewMode = ref(localStorage.getItem('whatsapp_templates_view_mode') || 'table'); // table | grid
const editingTemplate = ref(null);
const selectedTemplate = ref(null);
const showDeleteDialog = ref(false);
const templateToDelete = ref(null);
const isDeleting = ref(false);
const selectedDateRange = ref('LAST_7_DAYS');

// Filters
const FILTERS = ['ALL', 'APPROVED', 'PENDING', 'REJECTED'];

const filteredTemplates = computed(() => {
  if (activeFilter.value === 'ALL') return templates.value;
  return templates.value.filter(
    tpl => tpl.status?.toUpperCase() === activeFilter.value
  );
});

const statusCounts = computed(() => {
  const counts = { ALL: templates.value.length };
  FILTERS.slice(1).forEach(f => {
    counts[f] = templates.value.filter(
      tpl => tpl.status?.toUpperCase() === f
    ).length;
  });
  return counts;
});

const handleSwitchView = mode => {
  viewMode.value = mode;
  localStorage.setItem('whatsapp_templates_view_mode', mode);
};

const handleDateRangeChange = range => {
  selectedDateRange.value = range;
  fetchAnalytics();
};

const fetchAnalytics = async () => {
  const daysMap = {
    LAST_7_DAYS: 7,
    LAST_14_DAYS: 14,
    LAST_30_DAYS: 30,
    LAST_90_DAYS: 90,
  };
  const days = daysMap[selectedDateRange.value] || 7;
  const now = Math.floor(Date.now() / 1000);
  const start = now - days * 86400;

  try {
    const result = await store.dispatch(
      'inboxes/fetchWhatsAppTemplateAnalytics',
      {
        inboxId: props.inbox.id,
        params: {
          start_time: start,
          end_time: now,
          granularity: 'DAILY',
        },
      }
    );
    analyticsByTemplate.value = result.analytics_by_template || {};
  } catch (error) {
    analyticsByTemplate.value = {};
  }
};

// Fetch templates from Meta
const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const result = await store.dispatch('inboxes/fetchWhatsAppTemplates', {
      inboxId: props.inbox.id,
      params: { limit: 100 },
    });
    templates.value = result.templates || [];
    fetchAnalytics();
  } catch (error) {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Sync templates
const syncTemplates = async () => {
  isSyncing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.SYNC_SUCCESS'));
    await fetchTemplates();
  } catch (error) {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

// View template detail
const handleView = template => {
  selectedTemplate.value = template;
  currentView.value = 'detail';
};

// Create template
const handleCreate = async templateData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('inboxes/createWhatsAppTemplate', {
      inboxId: props.inbox.id,
      template: templateData,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.CREATE_SUCCESS'));
    currentView.value = 'list';
    await fetchTemplates();
  } catch (error) {
    const errorMsg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.CREATE_ERROR');
    useAlert(errorMsg);
  } finally {
    isSubmitting.value = false;
  }
};

// Edit template
const handleEdit = template => {
  editingTemplate.value = template;
  currentView.value = 'edit';
};

const handleUpdate = async templateData => {
  if (!editingTemplate.value) return;
  isSubmitting.value = true;
  try {
    await store.dispatch('inboxes/updateWhatsAppTemplate', {
      inboxId: props.inbox.id,
      templateId: editingTemplate.value.id,
      template: templateData,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.UPDATE_SUCCESS'));
    currentView.value = 'list';
    editingTemplate.value = null;
    await fetchTemplates();
  } catch (error) {
    const errorMsg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.UPDATE_ERROR');
    useAlert(errorMsg);
  } finally {
    isSubmitting.value = false;
  }
};

// Delete template
const confirmDelete = template => {
  templateToDelete.value = template;
  showDeleteDialog.value = true;
};

const handleDelete = async () => {
  if (!templateToDelete.value) return;
  isDeleting.value = true;
  try {
    await store.dispatch('inboxes/deleteWhatsAppTemplate', {
      inboxId: props.inbox.id,
      templateId: templateToDelete.value.id,
      templateName: templateToDelete.value.name,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.DELETE_SUCCESS'));
    if (selectedTemplate.value?.id === templateToDelete.value.id) {
      currentView.value = 'list';
      selectedTemplate.value = null;
    }
    await fetchTemplates();
  } catch (error) {
    const errorMsg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.API.DELETE_ERROR');
    useAlert(errorMsg);
  } finally {
    isDeleting.value = false;
    showDeleteDialog.value = false;
    templateToDelete.value = null;
  }
};

const cancelForm = () => {
  currentView.value = 'list';
  editingTemplate.value = null;
  selectedTemplate.value = null;
};

onMounted(fetchTemplates);
</script>

<template>
  <div class="w-full">
    <!-- Header -->
    <div v-if="currentView === 'list'" class="flex flex-col gap-2 mb-6">
      <h2 class="text-xl font-semibold text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION') }}
      </p>
    </div>

    <!-- List View -->
    <template v-if="currentView === 'list'">
      <!-- Category info banner -->
      <div
        class="flex items-start gap-3 p-4 mb-6 rounded-2xl bg-n-alpha-1 border border-n-weak"
      >
        <span
          class="i-lucide-info text-n-blue-11 text-lg flex-shrink-0 mt-0.5"
        />
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY_NOTE.TITLE') }}
          </span>
          <p class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY_NOTE.DESCRIPTION') }}
          </p>
          <a
            :href="metaBusinessUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11 hover:underline mt-1"
          >
            <span class="i-lucide-external-link text-xs" />
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINK.BUTTON') }}
          </a>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <Spinner size="" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="templates.length === 0"
        class="flex flex-col items-center justify-center py-20 gap-3"
      >
        <div
          class="w-16 h-16 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-file-text text-2xl text-n-slate-10" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 text-center max-w-md">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_STATE.DESCRIPTION') }}
        </p>
        <NextButton
          sm
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_BUTTON')"
          icon="i-lucide-plus"
          @click="currentView = 'create'"
        />
      </div>

      <!-- Table View (List Mode) -->
      <template v-else-if="viewMode === 'table'">
        <TemplateTable
          :templates="templates"
          :analytics="analyticsByTemplate"
          :waba-id="wabaId"
          :is-syncing="isSyncing"
          :selected-date-range="selectedDateRange"
          @create="currentView = 'create'"
          @sync="syncTemplates"
          @view="handleView"
          @edit="handleEdit"
          @delete="confirmDelete"
          @switch-view="handleSwitchView"
          @update-date-range="handleDateRangeChange"
        />
      </template>

      <!-- Grid View (Mosaic Mode) -->
      <template v-else-if="viewMode === 'grid'">
        <!-- Action bar -->
        <div class="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div class="flex items-center gap-2 flex-wrap">
            <NextButton
              v-for="filter in FILTERS"
              :key="filter"
              xs
              :ghost="activeFilter !== filter"
              :slate="activeFilter !== filter"
              :faded="activeFilter === filter"
              :label="`${$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FILTER.' + filter)} (${statusCounts[filter] || 0})`"
              @click="activeFilter = filter"
            />
          </div>
          <div class="flex items-center gap-2">
            <!-- View Switcher -->
            <div
              class="flex items-center h-8 p-0.5 rounded-xl bg-n-alpha-1 border border-n-weak"
            >
              <button
                type="button"
                v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.VIEW_MODE.GRID')"
                class="h-7 px-2 rounded-lg bg-n-solid-1 text-n-brand shadow-sm font-bold transition-all flex items-center justify-center"
                @click="handleSwitchView('grid')"
              >
                <span class="i-lucide-layout-grid text-sm block" />
              </button>
              <button
                type="button"
                v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.VIEW_MODE.TABLE')"
                class="h-7 px-2 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors flex items-center justify-center"
                @click="handleSwitchView('table')"
              >
                <span class="i-lucide-table text-sm block" />
              </button>
            </div>
            <NextButton
              sm
              ghost
              slate
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC_BUTTON')"
              icon="i-lucide-refresh-cw"
              :is-loading="isSyncing"
              @click="syncTemplates"
            />
            <NextButton
              sm
              :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE_BUTTON')"
              icon="i-lucide-plus"
              @click="currentView = 'create'"
            />
          </div>
        </div>

        <!-- Template grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TemplateCard
            v-for="template in filteredTemplates"
            :key="template.id"
            :template="template"
            :waba-id="wabaId"
            @view="handleView"
            @edit="handleEdit"
            @delete="confirmDelete"
          />
        </div>
      </template>
    </template>

    <!-- Detail View -->
    <template v-if="currentView === 'detail' && selectedTemplate">
      <TemplateDetail
        :template="selectedTemplate"
        :inbox-id="inbox.id"
        :waba-id="wabaId"
        :selected-date-range="selectedDateRange"
        @back="cancelForm"
        @edit="handleEdit"
        @delete="confirmDelete"
        @update-date-range="handleDateRangeChange"
      />
    </template>

    <!-- Create View -->
    <template v-if="currentView === 'create'">
      <div class="mb-4">
        <NextButton
          xs
          ghost
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BACK_TO_LIST')"
          icon="i-lucide-arrow-left"
          @click="cancelForm"
        />
      </div>
      <TemplateForm
        :inbox-id="inbox.id"
        :is-submitting="isSubmitting"
        @submit="handleCreate"
        @cancel="cancelForm"
      />
    </template>

    <!-- Edit View -->
    <template v-if="currentView === 'edit'">
      <div class="mb-4">
        <NextButton
          xs
          ghost
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.BACK_TO_LIST')"
          icon="i-lucide-arrow-left"
          @click="cancelForm"
        />
      </div>
      <TemplateForm
        :inbox-id="inbox.id"
        :existing-template="editingTemplate"
        :is-submitting="isSubmitting"
        @submit="handleUpdate"
        @cancel="cancelForm"
      />
    </template>

    <!-- Delete Confirmation Dialog -->
    <div
      v-if="showDeleteDialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-4"
    >
      <div
        class="bg-n-solid-2 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 flex flex-col gap-4"
      >
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{
            $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.MESSAGE', {
              templateName: templateToDelete?.name,
            })
          }}
        </p>
        <div class="flex items-center gap-3 justify-end">
          <NextButton
            ghost
            slate
            sm
            :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.CANCEL')"
            @click="showDeleteDialog = false"
          />
          <NextButton
            ruby
            sm
            :is-loading="isDeleting"
            :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.CONFIRM')"
            icon="i-lucide-trash-2"
            @click="handleDelete"
          />
        </div>
      </div>
    </div>
  </div>
</template>
